import React, { useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useSession } from 'session-context';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import BonusTracker from 'bonus-tracker-component';
import WeatherWindGenerator from 'weather-wind-generator';
import NavalEncounterGenerator from 'naval-encounter-generator';
import SeafaringTurnTracker from 'seafaring-turn-tracker';
import InteractiveShipHull from 'interactive-ship-hull';
import VesselSelector from './vessel-selector';
import CAPTracker from './cap-tracker-component';
import RulesReference from './rules-reference-component';
import { AlertTriangle, Ship, AnchorIcon, Wind, Shield, Radar, Book } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { VesselSection } from './types/vessel';
import { VESSEL_DATA } from './types/vessel';
import ShipStatusTracker from './ship-status-tracker';

type DeckSections = {
  upper: VesselSection[];
  middle?: VesselSection[];
  lower?: VesselSection[];
} & {
  [key: `middle${number}`]: VesselSection[];
};

const CampaignDashboard: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { 
    gameState, 
    updateGameState,
    isDM
  } = useSession();

  // Initialize or update deck sections and rigging sections when vessel changes
  useEffect(() => {
    if (gameState) {
      const vessel = VESSEL_DATA[gameState.selectedVessel || 'Caravel'];
      const newDeckSections: DeckSections = {
        upper: Array(vessel.sectionsPerDeck).fill({ 
          current: vessel.sectionHP, 
          max: vessel.sectionHP, 
          hasDealtHoleDamage: false 
        })
      };

      // For vessels with 2 decks, the lower deck is the second deck
      if (vessel.decks === 2) {
        newDeckSections.lower = Array(vessel.sectionsPerDeck).fill({ 
          current: vessel.sectionHP, 
          max: vessel.sectionHP, 
          hasDealtHoleDamage: false 
        });
      }
      // For vessels with 3 or more decks
      else if (vessel.decks >= 3) {
        // Add middle decks
        for (let i = 1; i < vessel.decks - 1; i++) {
          newDeckSections[`middle${i}`] = Array(vessel.sectionsPerDeck).fill({ 
            current: vessel.sectionHP, 
            max: vessel.sectionHP, 
            hasDealtHoleDamage: false 
          });
        }
        // Lower deck is the last deck
        newDeckSections.lower = Array(vessel.sectionsPerDeck).fill({ 
          current: vessel.sectionHP, 
          max: vessel.sectionHP, 
          hasDealtHoleDamage: false 
        });
      }

      const newRiggingSections = Array(vessel.riggingSections).fill({ 
        current: vessel.riggingHP, 
        max: vessel.riggingHP 
      });

      updateGameState('deckSections', newDeckSections);
      updateGameState('riggingSections', newRiggingSections);
    }
  }, [gameState?.selectedVessel]);

  // Check if we have a valid session
  if (!sessionId) {
    return <Navigate to="/" replace />;
  }

  // Check if we have game state
  if (!gameState) {
    return <div>Loading...</div>;
  }

  // Check if user is DM
  const userIsDM = isDM();

  const handleDeckUpdate = (deckType: 'upper' | 'lower' | `middle${number}`, sections: VesselSection[]) => {
    // Create a deep copy of the current deck sections
    const currentDeckSections: DeckSections = { ...gameState.deckSections };
    
    // Update the specific deck type
    currentDeckSections[deckType] = sections.map(section => ({ ...section }));
    
    // Update the game state with the new deck sections
    updateGameState('deckSections', currentDeckSections);
  };

  const handleAllDecksUpdate = (deckSections: DeckSections) => {
    // Update the game state with all deck sections at once
    updateGameState('deckSections', deckSections);
  };

  const handleRiggingUpdate = (sections: VesselSection[]) => {
    updateGameState('riggingSections', sections);
  };

  const handleWeatherGenerated = (weatherData: typeof gameState.weatherData) => {
    updateGameState('weatherData', weatherData);
  };

  const handleBonusesUpdate = (bonuses: typeof gameState.bonuses) => {
    updateGameState('bonuses', bonuses);
  };

  const handleRoleVacanciesUpdate = (roleVacancies: typeof gameState.roleVacancies) => {
    updateGameState('roleVacancies', roleVacancies);
  };

  // Handler functions for updating game state
  const handleMoraleChange = (moraleState: typeof gameState.moraleState) => {
    updateGameState('moraleState', moraleState);
  };
  
  const handleCleanlinessChange = (cleanlinessState: typeof gameState.cleanlinessState) => {
    updateGameState('cleanlinessState', cleanlinessState);
  };
  
  const handleNavigationChange = (navigationState: typeof gameState.navigationState) => {
    updateGameState('navigationState', navigationState);
  };
  
  const handleUserActivityChange = (uid: string, activityData: any) => {
    updateGameState('userActivities', { ...gameState.userActivities, [uid]: activityData });
  };
  
  const handleSharedDaysInTurnChange = (days: number) => {
    updateGameState('sharedDaysInTurn', days);
  };
  
  const handleVesselSelect = (vesselName: string) => {
    const vessel = VESSEL_DATA[vesselName];
    const newDeckSections: DeckSections = {
      upper: Array(vessel.sectionsPerDeck).fill({ 
        current: vessel.sectionHP, 
        max: vessel.sectionHP, 
        hasDealtHoleDamage: false 
      })
    };

    // For vessels with 2 decks, the lower deck is the second deck
    if (vessel.decks === 2) {
      newDeckSections.lower = Array(vessel.sectionsPerDeck).fill({ 
        current: vessel.sectionHP, 
        max: vessel.sectionHP, 
        hasDealtHoleDamage: false 
      });
    }
    // For vessels with 3 or more decks
    else if (vessel.decks >= 3) {
      // Add middle decks
      for (let i = 1; i < vessel.decks - 1; i++) {
        newDeckSections[`middle${i}`] = Array(vessel.sectionsPerDeck).fill({ 
          current: vessel.sectionHP, 
          max: vessel.sectionHP, 
          hasDealtHoleDamage: false 
        });
      }
      // Lower deck is the last deck
      newDeckSections.lower = Array(vessel.sectionsPerDeck).fill({ 
        current: vessel.sectionHP, 
        max: vessel.sectionHP, 
        hasDealtHoleDamage: false 
      });
    }

    const newRiggingSections = Array(vessel.riggingSections).fill({ 
      current: vessel.riggingHP, 
      max: vessel.riggingHP 
    });

    updateGameState('selectedVessel', vesselName);
    updateGameState('deckSections', newDeckSections);
    updateGameState('riggingSections', newRiggingSections);
  };

  // Create a placeholder component for non-DM users
  const DMToolsPlaceholder = () => (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          DM Tools Access
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">
          Weather generation and encounter tools are only available to the Dungeon Master.
        </p>
      </CardContent>
    </Card>
  );
  
  return (
    <div className="container mx-auto p-4">
      <Tabs defaultValue="cruise" className="w-full">
        <TabsList className="grid grid-cols-5 mb-6">
          <TabsTrigger value="cruise" className="flex items-center gap-2">
            <Ship className="h-4 w-4" />
            Cruise Management
          </TabsTrigger>
          <TabsTrigger value="combat" className="flex items-center gap-2">
            <AnchorIcon className="h-4 w-4" />
            Ship Combat
          </TabsTrigger>
          <TabsTrigger value="dm" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            DM Tools
          </TabsTrigger>
          <TabsTrigger value="bonuses" className="flex items-center gap-2">
            <Radar className="h-4 w-4" />
            Status Effects
          </TabsTrigger>
          <TabsTrigger value="rules" className="flex items-center gap-2">
            <Book className="h-4 w-4" />
            Rules Reference
          </TabsTrigger>
        </TabsList>

        {/* Cruise Management Tab */}
        <TabsContent value="cruise" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <SeafaringTurnTracker 
              onMoraleChange={handleMoraleChange}
              onCleanlinessChange={handleCleanlinessChange}
              onNavigationChange={handleNavigationChange}
              currentMorale={gameState.moraleState}
              currentCleanliness={gameState.cleanlinessState}
              currentNavigation={gameState.navigationState}
            />
            <CAPTracker
              sharedDaysInTurn={gameState.sharedDaysInTurn || 3}
              userActivities={gameState.userActivities || {}}
              onUserActivityChange={handleUserActivityChange}
              onSharedDaysInTurnChange={handleSharedDaysInTurnChange}
            />
          </div>
        </TabsContent>

        {/* Ship Combat Tab */}
        <TabsContent value="combat">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 mb-4">
              <VesselSelector
                onVesselSelect={handleVesselSelect}
                selectedVessel={gameState.selectedVessel || 'Caravel'}
              />
            </div>
            <div className="col-span-8">
              <InteractiveShipHull
                upperDeckImage="https://i.imgur.com/wxTzrKd.jpeg"
                bottomDeckImage="https://i.imgur.com/L0c4qpV.jpeg"
                selectedVessel={gameState.selectedVessel || 'Caravel'}
                deckSections={gameState.deckSections}
                riggingSections={gameState.riggingSections}
                onDeckUpdate={handleDeckUpdate}
                onRiggingUpdate={handleRiggingUpdate}
                onAllDecksUpdate={handleAllDecksUpdate}
              />
            </div>
            <div className="col-span-4">
              <ShipStatusTracker
                upperDeckSections={gameState.deckSections.upper}
                bottomDeckSections={gameState.deckSections.lower || []}
                riggingSections={gameState.riggingSections}
              />
            </div>
          </div>
        </TabsContent>

        {/* DM Tools Tab */}
        <TabsContent value="dm">
          {userIsDM ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <WeatherWindGenerator 
                onWeatherGenerated={handleWeatherGenerated}
                currentWeather={gameState.weatherData}
              />
              <NavalEncounterGenerator 
                weatherData={gameState.weatherData}
                crewMorale={gameState.moraleState}
                navigationState={gameState.navigationState}
                shipCleanliness={gameState.cleanlinessState}
              />
            </div>
          ) : (
            <DMToolsPlaceholder />
          )}
        </TabsContent>

        {/* Status Effects Tab */}
        <TabsContent value="bonuses">
          <BonusTracker 
            moraleState={gameState.moraleState} 
            cleanlinessState={gameState.cleanlinessState}
            bonuses={gameState.bonuses}
            roleVacancies={gameState.roleVacancies}
            onBonusesChange={handleBonusesUpdate}
            onRoleVacanciesChange={handleRoleVacanciesUpdate}
          />
        </TabsContent>

        {/* Rules Reference Tab */}
        <TabsContent value="rules">
          <RulesReference />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CampaignDashboard;